import { Select, useToast } from '@/components';
import { formatDateTime } from '@/lib/datetime';
import {
  ApiClientError,
  DATE_FORMATS,
  type DateFormat,
  TIME_FORMATS,
  type TimeFormat,
  type UpdateMeBody,
} from '@uchat-team-management/shared';
import { useUpdateMe } from './hooks';

const DATE_SAMPLES: Record<DateFormat, string> = {
  'DD/MM/YYYY': '31/12/2026',
  'MM/DD/YYYY': '12/31/2026',
  'YYYY-MM-DD': '2026-12-31',
  'DD MMM YYYY': '31 Dec 2026',
};
const TIME_LABELS: Record<TimeFormat, string> = {
  '24h': '24-hour — 14:30',
  '12h': '12-hour — 2:30 PM',
};

// Select values are strings, so Auto (null) travels as this sentinel.
const AUTO = 'auto';

export const DateTimeSettings = (props: {
  dateFormat: DateFormat | null;
  timeFormat: TimeFormat | null;
  timezone: string | null;
}) => {
  const update = useUpdateMe();
  const toast = useToast();

  // One field per PATCH; values derive from the session, so a failed save
  // simply keeps showing the persisted value — no local state to revert.
  const save = async (body: UpdateMeBody) => {
    try {
      await update.mutateAsync(body);
      toast.success('Preferences updated');
    } catch (err) {
      toast.error(
        err instanceof ApiClientError
          ? err.error.message
          : ((err as Error).message ?? 'Update failed'),
      );
    }
  };

  const dateOptions = [
    { value: AUTO, label: 'Auto (browser default)' },
    ...DATE_FORMATS.map((f) => ({ value: f, label: `${f} — ${DATE_SAMPLES[f]}` })),
  ];
  const timeOptions = [
    { value: AUTO, label: 'Auto (browser default)' },
    ...TIME_FORMATS.map((f) => ({ value: f, label: TIME_LABELS[f] })),
  ];
  const timezoneOptions = [
    { value: AUTO, label: `Auto (${Intl.DateTimeFormat().resolvedOptions().timeZone})` },
    ...Intl.supportedValuesOf('timeZone').map((z) => ({ value: z, label: z })),
  ];

  return (
    <div class="flex flex-col gap-4">
      <Select
        label="Date format"
        options={dateOptions}
        value={props.dateFormat ?? AUTO}
        onChange={(v) => save({ dateFormat: v === AUTO ? null : (v as DateFormat) })}
        disabled={update.isPending}
      />
      <Select
        label="Time format"
        options={timeOptions}
        value={props.timeFormat ?? AUTO}
        onChange={(v) => save({ timeFormat: v === AUTO ? null : (v as TimeFormat) })}
        disabled={update.isPending}
      />
      <Select
        label="Timezone"
        options={timezoneOptions}
        value={props.timezone ?? AUTO}
        onChange={(v) => save({ timezone: v === AUTO ? null : v })}
        disabled={update.isPending}
      />
      <p class="text-sm text-neutral-500 transition-opacity duration-200 ease-out starting:opacity-0 motion-reduce:transition-none dark:text-neutral-400">
        Example: {formatDateTime(new Date().toISOString())}
      </p>
    </div>
  );
};
